import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import type { Order, OrderItem } from './orderDetailTypes';
import { ALL_STATUSES, formatHistoryEvent, ORDER_STATUS_KEYS, statusColors } from './orderDetailTypes';

export interface OrderDetailPanelProps {
  order: Order;
  replaceOrder: (o: Order) => void;
  reloadOrder: () => Promise<Order | null>;
}

export default function OrderDetailPanel({ order, replaceOrder, reloadOrder }: OrderDetailPanelProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [editingEta, setEditingEta] = useState(false);
  const [etaMinutes, setEtaMinutes] = useState<number>(45);
  const [updatingEta, setUpdatingEta] = useState(false);

  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const [orderHistory, setOrderHistory] = useState<
    Array<{ eventType: string; fromValue?: string; toValue?: string; note?: string; createdAt: string; performedBy?: { name?: string; email?: string } }>
  >([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!order._id) {
      setOrderHistory([]);
      return;
    }
    let cancelled = false;
    setLoadingHistory(true);
    api
      .get(`/admin/orders/${order._id}/history`)
      .then((res) => {
        if (!cancelled && res.data?.data) setOrderHistory(res.data.data);
      })
      .catch(() => {
        if (!cancelled) setOrderHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order._id]);

  const fetchOrderHistory = async (orderId: string) => {
    try {
      const res = await api.get(`/admin/orders/${orderId}/history`);
      if (res.data?.data) setOrderHistory(res.data.data);
    } catch {
      setOrderHistory([]);
    }
  };

  const updateOrderEta = async (orderId: string, minutesFromNow: number) => {
    try {
      setUpdatingEta(true);
      const estimatedDeliveryTime = new Date(Date.now() + minutesFromNow * 60 * 1000).toISOString();
      await api.patch(`/orders/${orderId}`, { estimatedDeliveryTime });
      const u = await reloadOrder();
      if (u) replaceOrder(u);
      setEditingEta(false);
    } catch (err) {
      console.error('Failed to update ETA', err);
    } finally {
      setUpdatingEta(false);
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string, rejectionReasonArg?: string) => {
    try {
      setUpdatingStatus(true);
      await api.patch(`/orders/${orderId}`, {
        status: newStatus,
        ...(rejectionReasonArg && { rejectionReason: rejectionReasonArg }),
      });
      const u = await reloadOrder();
      if (u) replaceOrder(u);
      await fetchOrderHistory(orderId);
    } catch (error: unknown) {
      console.error('Error updating order status:', error);
      const msg = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      alert(msg || t('orders.failedToUpdateStatus'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openRefundModal = () => {
    const total = order.totalPriceEGP ?? 0;
    const refunded = order.refundedAmountEGP ?? 0;
    const max = total - refunded;
    setRefundAmount(max > 0 ? String(max) : '');
    setShowRefundModal(true);
  };

  const submitRefund = async () => {
    const total = order.totalPriceEGP ?? 0;
    const refunded = order.refundedAmountEGP ?? 0;
    const maxRefund = total - refunded;
    if (maxRefund <= 0) {
      alert(t('orders.orderAlreadyRefunded'));
      return;
    }
    const amount = refundAmount.trim() ? parseFloat(refundAmount) : undefined;
    if (amount !== undefined && (isNaN(amount) || amount <= 0 || amount > maxRefund)) {
      alert(t('orders.enterRefundAmount', { max: maxRefund.toFixed(2) }));
      return;
    }
    try {
      setRefundSubmitting(true);
      await api.post(`/admin/orders/${order._id}/refund`, amount != null ? { amountEGP: amount } : {});
      const u = await reloadOrder();
      if (u) replaceOrder(u);
      setShowRefundModal(false);
    } catch (error: unknown) {
      console.error('Error refunding order:', error);
      const msg = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      alert(msg || t('orders.failedToProcessRefund'));
    } finally {
      setRefundSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAddress = (address: Order['deliveryAddress']) => {
    const parts = [
      address.street,
      address.building && `Building ${address.building}`,
      address.floor && `Floor ${address.floor}`,
      address.apartment && `Apartment ${address.apartment}`,
      address.city,
      address.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getStatusLabel = (status: string) => {
    const key = ORDER_STATUS_KEYS[status];
    return key ? t(`orders.${key}`) : status;
  };

  const paymentMethodLabel =
    order.paymentMethod === 'cod'
      ? t('orders.cashOnDelivery')
      : order.paymentMethod === 'instapay'
        ? 'InstaPay'
        : order.paymentMethod === 'wallet'
          ? t('manualPayments.mobileWallet')
          : t('orders.cashOnDelivery');

  return (
    <>
      <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">{t('orders.customerOrderRef')}</label>
            <p className="text-sm font-mono text-gray-900">#{order.orderNumber ?? order._id.substring(0, 8)}</p>
            {order.internalOrderSequence != null && (
              <p className="text-xs text-gray-500 mt-1 font-mono">
                {t('orders.internalOrderSequence')}: #{order.internalOrderSequence}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('orders.status')}</label>
            <p>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                {getStatusLabel(order.status)}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('orders.date')}</label>
            <p className="text-sm text-gray-900">{formatDate(order.createdAt)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('orders.paymentMethod')}</label>
            <p className="text-sm text-gray-900">{paymentMethodLabel}</p>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-500">{t('orders.customer')}</label>
          <p className="text-sm text-gray-900">{order.userId?.name || 'N/A'}</p>
          <p className="text-sm text-gray-500">{order.userId?.email || 'N/A'}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-500">
            {order.items?.length ? t('orders.items') : t('orders.recipe')}
          </label>
          {order.items?.length ? (
            <ul className="mt-1 space-y-2">
              {order.items.map((item: OrderItem, idx: number) => (
                <li key={idx} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                  {item.recipeImage && (
                    <img src={item.recipeImage} alt={item.recipeTitle} className="h-12 w-12 rounded object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{item.recipeTitle}</p>
                    <p className="text-sm text-gray-500">
                      Qty: {item.quantity} × {item.unitPriceEGP.toFixed(2)} EGP = {item.lineTotalEGP.toFixed(2)} EGP
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center mt-1">
              {order.recipeImage && (
                <img src={order.recipeImage} alt={order.recipeTitle ?? 'Recipe'} className="h-16 w-16 rounded object-cover mr-3" />
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">{order.recipeTitle ?? '—'}</p>
                <p className="text-sm text-gray-500">
                  {t('orders.quantity')}: {order.quantity ?? '—'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-500">{t('orders.deliveryAddress')}</label>
          <p className="text-sm text-gray-900 mt-1">
            <strong>{order.deliveryAddress.label}</strong>
          </p>
          <p className="text-sm text-gray-700">{formatAddress(order.deliveryAddress)}</p>
          {order.deliveryAddress.phoneNumber && (
            <p className="text-sm text-gray-500 mt-1">
              {t('orders.phone')}: {order.deliveryAddress.phoneNumber}
            </p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-500">{t('orders.estimatedDelivery')}</label>
          {!editingEta ? (
            <div className="mt-1 flex items-center gap-2">
              <p className="text-sm text-gray-900">
                {order.estimatedDeliveryTime
                  ? new Date(order.estimatedDeliveryTime).toLocaleString(undefined, {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })
                  : '—'}
              </p>
              {order.status !== 'delivered' && order.status !== 'cancelled' && order.status !== 'rejected' && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingEta(true);
                    if (order.estimatedDeliveryTime) {
                      const mins = Math.round((new Date(order.estimatedDeliveryTime).getTime() - Date.now()) / 60000);
                      setEtaMinutes(mins > 0 ? mins : 45);
                    } else {
                      setEtaMinutes(45);
                    }
                  }}
                  className="text-sm text-primary-orange hover:underline"
                >
                  {t('common.edit')}
                </button>
              )}
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input
                type="number"
                min="5"
                max="300"
                value={etaMinutes}
                onChange={(e) => setEtaMinutes(parseInt(e.target.value, 10) || 45)}
                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <span className="text-sm text-gray-600">{t('orders.minutesFromNow')}</span>
              <button
                type="button"
                onClick={() => updateOrderEta(order._id, etaMinutes)}
                disabled={updatingEta}
                className="px-3 py-1 bg-primary-orange text-white rounded text-sm hover:opacity-90 disabled:opacity-50"
              >
                {updatingEta ? t('orders.saving') : t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => setEditingEta(false)}
                disabled={updatingEta}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
            </div>
          )}
        </div>

        <div className="border-t pt-4">
          {order.subtotalEGP != null && (
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-500">{t('orders.subtotal')}</span>
              <span className="text-sm text-gray-900">{order.subtotalEGP.toFixed(2)} EGP</span>
            </div>
          )}
          {(order.quantity != null || order.items?.length) && (
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-500">{t('orders.itemsQty')}</span>
              <span className="text-sm text-gray-900">
                {order.items?.length
                  ? `${order.items.length} item(s), ${order.items.reduce((s: number, i: OrderItem) => s + i.quantity, 0)} total`
                  : order.quantity}
              </span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>{t('orders.total')}</span>
            <span>{order.totalPriceEGP.toFixed(2)} EGP</span>
          </div>
          {(order.refundedAmountEGP ?? 0) > 0 && (
            <div className="flex justify-between text-sm text-amber-700 mt-2">
              <span>{t('orders.refundToWallet')}</span>
              <span>{order.refundedAmountEGP!.toFixed(2)} EGP</span>
            </div>
          )}
        </div>

        {(order.refundedAmountEGP ?? 0) < (order.totalPriceEGP ?? 0) && (
          <div className="border-t pt-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('orders.refundToWallet')}</label>
            <p className="text-xs text-gray-500 mb-2">{t('orders.refundDescription')}</p>
            <button
              type="button"
              onClick={openRefundModal}
              className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium hover:bg-amber-200"
            >
              {t('orders.refundToWallet')}
            </button>
          </div>
        )}

        {order.notes && (
          <div>
            <label className="text-sm font-medium text-gray-500">{t('orders.notes')}</label>
            <p className="text-sm text-gray-900 mt-1">{order.notes}</p>
          </div>
        )}

        {order.rejectionReason && (
          <div>
            <label className="text-sm font-medium text-red-500">{t('orders.rejectionReason')}</label>
            <p className="text-sm text-red-700 mt-1">{order.rejectionReason}</p>
          </div>
        )}

        <div className="border-t pt-4" onClick={(e) => e.stopPropagation()}>
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-base font-semibold text-amber-900">{t('orders.statusSequentialHint')}</p>
          </div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">{t('orders.changeStatus')}</label>
          <div className="flex gap-2 items-center flex-wrap">
            <select
              value={order.status}
              onChange={(e) => {
                e.preventDefault();
                const newStatus = e.target.value;
                if (newStatus === 'rejected') {
                  setRejectReason('');
                  setShowRejectModal(true);
                } else {
                  updateOrderStatus(order._id, newStatus);
                }
              }}
              disabled={updatingStatus}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent min-w-[180px]"
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {getStatusLabel(s)}
                </option>
              ))}
            </select>
            {updatingStatus && <span className="text-sm text-gray-500">{t('orders.statusUpdating')}</span>}
          </div>
          <p className="text-xs text-gray-500 mt-1">{t('orders.statusUpdateHint')}</p>
        </div>

        <div className="border-t pt-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">{t('orders.orderHistory')}</label>
          {loadingHistory ? (
            <p className="text-sm text-gray-500">{t('orders.loadingHistory')}</p>
          ) : orderHistory.length === 0 ? (
            <p className="text-sm text-gray-500">{t('orders.noHistory')}</p>
          ) : (
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {orderHistory.map((entry, idx) => (
                <li key={idx} className="flex flex-col gap-0.5 text-sm border-l-2 border-gray-200 pl-3 py-1">
                  <span className="text-gray-900 font-medium">{formatHistoryEvent(t, entry)}</span>
                  <span className="text-gray-500 text-xs">
                    {new Date(entry.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                    {entry.performedBy?.name && ` · ${entry.performedBy.name}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className={`relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('orders.rejectOrderTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('orders.rejectOrderDescription')}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.rejectionReasonLabel')}</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                placeholder={t('orders.rejectionReasonPlaceholder')}
              />
            </div>
            <div className={`flex gap-2 justify-end ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={updatingStatus}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  await updateOrderStatus(order._id, 'rejected', rejectReason.trim() || undefined);
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={updatingStatus}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {updatingStatus ? t('orders.statusUpdating') : t('orders.confirmReject')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRefundModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('orders.refundToWallet')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('orders.orderSummary', { id: order.orderNumber ?? order._id.substring(0, 8), total: order.totalPriceEGP.toFixed(2) })}
              {(order.refundedAmountEGP ?? 0) > 0 && (
                <span className="block mt-1">{t('orders.alreadyRefundedLabel', { amount: order.refundedAmountEGP!.toFixed(2) })}</span>
              )}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.amountToRefund')}</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={(order.totalPriceEGP ?? 0) - (order.refundedAmountEGP ?? 0)}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                placeholder={t('orders.maxRefund', {
                  max: ((order.totalPriceEGP ?? 0) - (order.refundedAmountEGP ?? 0)).toFixed(2),
                })}
              />
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowRefundModal(false)}
                disabled={refundSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={submitRefund}
                disabled={refundSubmitting}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50"
              >
                {refundSubmitting ? t('orders.processingRefund') : t('orders.refundBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
